import { FormGroup, Input } from '@snsw/react-component-library';
import { Textarea } from 'components/Textarea';
import { ChangeEvent, ForwardedRef, forwardRef } from 'react';
import { QuestionProps, TextQuestionProps } from '../types';
import { getLabelText } from './getLabelText';
import { QuestionConditionals } from './QuestionConditionals';

type Props = QuestionProps<string> & TextQuestionProps;

// The FormGroup component doesn't support a fragment inside it, so we need to render the input field in
// a separate component.
type TextQuestionInputProps = Pick<Props, 'id' | 'name' | 'responseLength' | 'value' | 'onChange' | 'inputWidth'> & {
  hasError?: boolean;
};

const TextQuestionInput = forwardRef(
  (
    { responseLength, value, onChange, ...inputProps }: TextQuestionInputProps,
    ref: ForwardedRef<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const fieldValue = value == null ? '' : value;

    const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange(event.target.value);
    };

    if (responseLength !== 'short') {
      const rows = responseLength === 'long' ? 'large' : 'small';

      return (
        <Textarea
          ref={ref as ForwardedRef<HTMLTextAreaElement>}
          {...inputProps}
          rows={rows}
          value={fieldValue}
          onChange={handleChange}
        />
      );
    }

    return (
      <Input
        {...inputProps}
        type="string"
        inputRef={ref as ForwardedRef<HTMLInputElement>}
        value={fieldValue}
        onChange={handleChange}
      />
    );
  }
);

export const TextQuestion = forwardRef((props: Props, ref: ForwardedRef<HTMLInputElement>) => {
  const {
    id,
    label,
    helpMessage,
    optional,
    errorMessage,
    value,
    conditionalMessages,
    supportingDocuments,
    ...inputProps
  } = props;

  const labelText = getLabelText(label, optional);

  return (
    <>
      <FormGroup
        id={`${id}-form-group`}
        label={labelText}
        helpMessage={helpMessage}
        hasError={!!errorMessage}
        errorMessage={errorMessage}
      >
        <TextQuestionInput id={id} ref={ref} value={value} hasError={!!errorMessage} {...inputProps} />
      </FormGroup>

      <QuestionConditionals
        value={value}
        supportingDocuments={supportingDocuments}
        conditionalMessages={conditionalMessages}
      />
    </>
  );
});

TextQuestion.defaultProps = {
  responseLength: 'short',
};
